import { useState } from 'react';
import { Header } from '@/components/layout/header';
import { LoadingState } from '@/components/loading-state';
import { ErrorState } from '@/components/error-state';
import { DailyCostChart, ServiceCostChart } from '@/components/cost-chart';
import { MetricTooltip } from '@/components/metric-tooltip';
import { useApi } from '@/hooks/use-api';
import { formatCurrency, formatPercent } from '@/lib/format';
import { TrendingUp, TrendingDown, AlertTriangle, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface CostSummary {
  currentMonthTotal: number;
  lastMonthTotal: number;
  dailyAverage: number;
  projectedMonthTotal: number;
  monthOverMonthPercent: number;
  daysElapsed: number;
}

interface ServiceCost {
  services: { service: string; amount: number }[];
}
interface DailyCost {
  days: { date: string; amount: number }[];
}
interface Anomaly {
  id: string;
  startDate: string;
  endDate: string;
  score: number;
  impact: number;
  rootCauses: { service: string; region: string }[];
}

function getAnomalySuggestion(service: string): string | null {
  const s = service.toLowerCase();
  if (s.includes('ec2'))
    return 'Look for forgotten devbox instances or an accidental instance type upgrade.';
  if (s.includes('rds'))
    return 'Check if storage auto-scaled or if a snapshot/backup ran unexpectedly.';
  if (s.includes('s3'))
    return 'Look for unusual data transfer or a large number of API requests (sync loops).';
  if (s.includes('cloudfront'))
    return 'Check for a traffic spike or bot crawling. Review CloudFront access logs.';
  if (s.includes('data transfer'))
    return 'Cross-region or internet data transfer can spike during large syncs or deploys.';
  if (s.includes('route 53') || s.includes('route53'))
    return 'DNS query volume spike — possibly a misconfigured health check or external scraping.';
  return 'Review the service in the AWS Cost Explorer console for detailed line items.';
}

export function CostsPage() {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const summary = useApi<CostSummary>('/api/costs/summary');
  const serviceUrl = selectedDate
    ? `/api/costs/by-service?date=${selectedDate}`
    : '/api/costs/by-service';
  const services = useApi<ServiceCost>(serviceUrl);
  const daily = useApi<DailyCost>('/api/costs/daily?months=2');
  const anomalies = useApi<{ anomalies: Anomaly[] }>('/api/costs/anomalies');

  const loading = summary.loading || services.loading || daily.loading;
  const anyError = summary.error || services.error || daily.error;

  function handleRefresh() {
    fetch('/api/cache/invalidate', { method: 'POST' })
      .catch(() => {})
      .finally(() => {
        summary.refresh();
        services.refresh();
        daily.refresh();
        anomalies.refresh();
      });
  }

  return (
    <div>
      <Header
        title="Cost Overview"
        lastFetched={summary.lastFetched}
        onRefresh={handleRefresh}
        loading={loading}
      />
      <div className="space-y-6 p-6">
        {anyError && <ErrorState message={anyError} onRetry={handleRefresh} />}
        {loading && !summary.data && <LoadingState rows={4} />}

        {summary.data && (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:gap-10">
            {/* Primary metric — the number that matters most */}
            <div>
              <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                Monthly Total
                <MetricTooltip metricKey="cost.monthly_total" />
              </div>
              <p className="text-3xl font-semibold tracking-tight">
                {formatCurrency(summary.data.currentMonthTotal)}
              </p>
              <p className="text-muted-foreground text-xs">
                {summary.data.daysElapsed} days in &middot; Projected{' '}
                {formatCurrency(summary.data.projectedMonthTotal)}
              </p>
            </div>

            {/* Supporting metrics — lighter weight */}
            <div className="flex gap-8 border-l pl-8">
              <div>
                <p className="text-muted-foreground text-xs">Daily Average</p>
                <p className="text-lg font-semibold">{formatCurrency(summary.data.dailyAverage)}</p>
              </div>
              <div>
                <div className="text-muted-foreground flex items-center gap-1 text-xs">
                  vs. Last Month
                  {summary.data.monthOverMonthPercent > 0 ? (
                    <TrendingUp className="h-3 w-3" style={{ color: 'var(--status-danger-fg)' }} />
                  ) : (
                    <TrendingDown className="text-muted-foreground h-3 w-3" />
                  )}
                </div>
                <p className="text-lg font-semibold">
                  {formatPercent(summary.data.monthOverMonthPercent)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Last Month</p>
                <p className="text-lg font-semibold">
                  {formatCurrency(summary.data.lastMonthTotal)}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-2">
          {daily.data && (
            <DailyCostChart
              data={daily.data.days}
              selectedDate={selectedDate}
              onDateClick={setSelectedDate}
            />
          )}
          {services.data && (
            <ServiceCostChart data={services.data.services} selectedDate={selectedDate} />
          )}
        </div>

        {anomalies.data && anomalies.data.anomalies.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <AlertTriangle className="h-4 w-4" style={{ color: 'var(--status-warning-fg)' }} />
                Unusual Spending (Last 30 Days)
                <span className="ml-auto flex items-center gap-1">
                  <Info className="text-muted-foreground h-3.5 w-3.5" />
                  <span className="text-muted-foreground text-xs font-normal">
                    AWS detected spending that deviates from your typical pattern
                  </span>
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {anomalies.data.anomalies.map((a) => {
                  const service = a.rootCauses?.[0]?.service ?? 'Unknown service';
                  const severity = a.impact > 20 ? 'High' : a.impact > 5 ? 'Medium' : 'Low';
                  const suggestion = getAnomalySuggestion(service);

                  return (
                    <div key={a.id} className="rounded-md border p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-block h-2 w-2 rounded-full"
                            style={{
                              backgroundColor:
                                severity === 'High'
                                  ? 'var(--status-danger-fg)'
                                  : severity === 'Medium'
                                    ? 'var(--status-warning-fg)'
                                    : 'var(--muted-foreground)',
                            }}
                          />
                          <p className="text-sm font-medium">{service}</p>
                        </div>
                        <p
                          className="text-sm font-medium"
                          style={{ color: 'var(--status-danger-fg)' }}
                        >
                          +{formatCurrency(a.impact)} over expected
                        </p>
                      </div>
                      <div className="mt-1.5 flex items-center justify-between">
                        <p className="text-muted-foreground text-xs">
                          {a.startDate} — {a.endDate} &middot; {severity} impact
                        </p>
                      </div>
                      {suggestion && (
                        <p className="text-muted-foreground mt-1.5 text-xs">
                          <span className="font-medium">Check:</span> {suggestion}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
